import React from "react"
import { UseFormRegister, FieldErrors, Path } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { getLocalizedText } from "utils/i18n/get-localized-text"
import { PlaceOrderFormData, Question } from "types"
import {
  isEmailField,
  shouldDisableContactField,
  getFieldMaxLength,
  getFieldMaxLengthMessageKey,
  getFieldMaxLengthDefaultMessage,
  EMAIL_VALIDATION_PATTERN,
} from "utils/form-submission/contact-field-utils"

interface FreeResponseQuestionProps {
  question: Question
  register: UseFormRegister<PlaceOrderFormData>
  errors: FieldErrors<PlaceOrderFormData>
  ticketId: number
  isFirstTicket: boolean
  isSameAsContact: boolean
}

const FreeResponseQuestion: React.FC<FreeResponseQuestionProps> = ({
  question,
  register,
  errors,
  ticketId,
  isFirstTicket,
  isSameAsContact,
}) => {
  const { t } = useTranslation()

  const questionLabel = getLocalizedText(question.text, "Question")
  const isEmailFieldType = isEmailField(questionLabel)

  const fieldName =
    `tickets.${ticketId}.${question.uuid}` as Path<PlaceOrderFormData>
  const fieldError = errors.tickets?.[ticketId]?.[question.uuid]

  const disabled = shouldDisableContactField(
    question.editable,
    isSameAsContact,
    isFirstTicket
  )

  return (
    <div>
      <label
        htmlFor={`text-${question.uuid}`}
        className="block mb-1 text-sm font-medium text-theme_text"
      >
        {questionLabel}
        {question.requires_answer && (
          <span
            className="ml-1 text-red-500"
            aria-label={t("validation.required", "Required")}
          >
            *
          </span>
        )}
      </label>
      <input
        id={`text-${question.uuid}`}
        type={isEmailFieldType && !question.editable ? "email" : "text"}
        className={`w-full border border-input_border rounded-md p-2 ${
          disabled
            ? "cursor-not-allowed bg-theme_background_lighter text-theme_text_medium_plus"
            : ""
        }`}
        disabled={disabled}
        aria-describedby={fieldError ? `${question.uuid}-error` : undefined}
        aria-invalid={fieldError ? "true" : "false"}
        aria-required={question.requires_answer ? "true" : "false"}
        {...register(fieldName, {
          required: question.requires_answer
            ? t("validation.field_required", "This field is required")
            : false,
          maxLength: {
            value: getFieldMaxLength(questionLabel),
            message: t(
              getFieldMaxLengthMessageKey(questionLabel),
              getFieldMaxLengthDefaultMessage(questionLabel)
            ),
          },
          ...(isEmailFieldType && !question.editable
            ? {
                pattern: {
                  value: EMAIL_VALIDATION_PATTERN,
                  message: t(
                    "validation.email_invalid",
                    "Invalid email address"
                  ),
                },
              }
            : {}),
        })}
      />
      {fieldError && (
        <p
          className="mt-1 text-sm text-red-500"
          role="alert"
          id={`${question.uuid}-error`}
        >
          {fieldError.message}
        </p>
      )}
    </div>
  )
}

export { FreeResponseQuestion }
